import axios from "axios";
import { supabase } from "./supabaseClient";

// Obter API URL do sistema de configuração dinâmica
const getApiUrl = () => {
  return (
    window.ENV?.REACT_APP_API_URL ||
    process.env.REACT_APP_API_URL ||
    "/api"
  );
};

const API_URL = getApiUrl();

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Helper para obter token de autenticação
const getAuthToken = async () => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new Error("Usuário não autenticado. Faça login.");
  }
  return token;
};

export const productParametersService = {
  // ========================================
  // TEMPLATES
  // ========================================

  /**
   * Listar todos os templates de um produto
   */
  async getTemplates(productId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/templates/${productId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar templates"
      );
    }
  },

  /**
   * Buscar template ativo de um produto
   */
  async getActiveTemplate(productId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/templates/${productId}/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar template ativo"
      );
    }
  },

  /**
   * Criar novo template
   */
  async createTemplate(templateData) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.post(
        `/product-parameters/templates`,
        templateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao criar template"
      );
    }
  },

  /**
   * Atualizar template existente
   */
  async updateTemplate(templateId, templateData) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.put(
        `/product-parameters/templates/${templateId}`,
        templateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao atualizar template"
      );
    }
  },

  /**
   * Ativar template
   */
  async activateTemplate(templateId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.put(
        `/product-parameters/templates/${templateId}/activate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao ativar template"
      );
    }
  },

  /**
   * Desativar template
   */
  async deactivateTemplate(templateId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.put(
        `/product-parameters/templates/${templateId}/deactivate`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao desativar template"
      );
    }
  },

  /**
   * Deletar template
   */
  async deleteTemplate(templateId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.delete(
        `/product-parameters/templates/${templateId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao deletar template"
      );
    }
  },

  // ========================================
  // RESPONSES
  // ========================================

  /**
   * Buscar respostas de um item de produção
   */
  async getResponses(itemId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/responses/${itemId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar respostas"
      );
    }
  },

  /**
   * Buscar respostas preenchidas de um lote de produção
   */
  async getResponsesByLot(lotId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/responses/by-lot/${lotId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar respostas do lote"
      );
    }
  },

  /**
   * Salvar respostas de parâmetros
   */
  async saveResponses(responseData) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.post(
        `/product-parameters/responses`,
        responseData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao salvar respostas"
      );
    }
  },

  // ========================================
  // REPORTS
  // ========================================

  /**
   * Buscar relatório de parâmetros
   */
  async getReports(filters = {}) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/reports`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: filters,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar relatórios"
      );
    }
  },

  /**
   * Buscar resumo de parâmetros agrupados
   */
  async getReportsSummary(filters = {}) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/reports/summary`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: filters,
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar resumo"
      );
    }
  },

  // ========================================
  // UTILITY
  // ========================================

  /**
   * Verificar se produto tem parâmetros ativos
   */
  async checkProductHasParameters(productId) {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/check/${productId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.has_active_parameters;
    } catch (error) {
      console.error("Erro ao verificar parâmetros:", error);
      return false;
    }
  },

  /**
   * Listar produtos que têm parâmetros ativos
   */
  async getProductsWithParameters() {
    try {
      const token = await getAuthToken();
      const response = await axiosInstance.get(
        `/product-parameters/products-with-parameters`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Erro ao buscar produtos com parâmetros"
      );
    }
  },
};

// Tipos de parâmetros suportados
export const PARAMETER_TYPES = [
  { value: "text", label: "Texto", icon: "Aa" },
  { value: "integer", label: "Número Inteiro", icon: "123" },
  { value: "float", label: "Número Decimal", icon: "1.5" },
  { value: "checkbox", label: "Checkbox (Sim/Não)", icon: "✓" },
  { value: "single_select", label: "Seleção Única", icon: "○" },
  { value: "multi_select", label: "Seleção Múltipla", icon: "☐" },
  { value: "date", label: "Data", icon: "📅" },
  { value: "datetime", label: "Data e Hora", icon: "🕐" },
  { value: "time", label: "Hora", icon: "⏰" },
  { value: "calculated", label: "Calculado (soma de outros campos)", icon: "Σ" },
];

// Helper para obter label do tipo
export const getParameterTypeLabel = (type) => {
  const paramType = PARAMETER_TYPES.find((t) => t.value === type);
  return paramType ? paramType.label : type;
};

// Helper para validar valor de parâmetro
export const validateParameterValue = (parameter, value) => {
  if (parameter.required && (value === undefined || value === null || value === "")) {
    return { valid: false, error: "Campo obrigatório" };
  }

  if (value === undefined || value === null || value === "") {
    return { valid: true };
  }

  switch (parameter.type) {
    case "integer":
      if (!Number.isInteger(Number(value))) {
        return { valid: false, error: "Deve ser um número inteiro" };
      }
      if (parameter.min !== undefined && Number(value) < parameter.min) {
        return { valid: false, error: `Valor mínimo: ${parameter.min}` };
      }
      if (parameter.max !== undefined && Number(value) > parameter.max) {
        return { valid: false, error: `Valor máximo: ${parameter.max}` };
      }
      break;

    case "float":
      if (isNaN(Number(value))) {
        return { valid: false, error: "Deve ser um número válido" };
      }
      if (parameter.min !== undefined && Number(value) < parameter.min) {
        return { valid: false, error: `Valor mínimo: ${parameter.min}` };
      }
      if (parameter.max !== undefined && Number(value) > parameter.max) {
        return { valid: false, error: `Valor máximo: ${parameter.max}` };
      }
      break;

    case "single_select":
      if (parameter.options && !parameter.options.includes(value)) {
        return { valid: false, error: "Opção inválida" };
      }
      break;

    case "multi_select":
      if (parameter.options && Array.isArray(value)) {
        const invalidOptions = value.filter((v) => !parameter.options.includes(v));
        if (invalidOptions.length > 0) {
          return { valid: false, error: "Opções inválidas selecionadas" };
        }
      }
      break;

    default:
      break;
  }

  return { valid: true };
};
